/**
 * BuchhaltungPage — konsolidierter Buchhaltungs-Bereich für Händler.
 *  - Offene Posten (Debitoren) mit Aging-Übersicht & Mahnwesen  → ./buchhaltung/OpenItemsTable
 *  - Kreditoren (Eingangsrechnungen, 3-Wege-Abgleich, SEPA/Skonto) → ./buchhaltung/KreditorenTab
 *  - Zahlungsimport (Summen-/Saldenliste CSV)                   → ./buchhaltung/PaymentsImport
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Calculator, Loader2, FileDown, Landmark, ArrowLeft, Gavel, FileSpreadsheet, FileArchive, Mail, Search, X, ShieldCheck, ShieldAlert, Truck, ScanLine, Plug, ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import api, { formatPrice, setApiOrgBranch } from "../lib/api";
import { useAuth } from "../lib/auth";
import { SettingsShortcut } from "../components/dealer/SettingsShortcut";
import EmployeeContextBanner from "../components/vermittler/EmployeeContextBanner";
import { previewPdfResponse, customerRecipient } from "../lib/pdfPreview";
import OrgBranchSwitcher from "../components/org/OrgBranchSwitcher";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import OpenItemsTable from "./buchhaltung/OpenItemsTable";
import PaymentsImport from "./buchhaltung/PaymentsImport";
import KreditorenTab from "./buchhaltung/KreditorenTab";
import ECReceiptsTab from "./buchhaltung/ECReceiptsTab";
import BoniTrackingTab from "./buchhaltung/BoniTrackingTab";
import OutstandingBankFundsTab from "./buchhaltung/OutstandingBankFundsTab";
import InternalInvoicesTab from "../components/buchhaltung/InternalInvoicesTab";
import KiBuchungssatzTab from "./buchhaltung/KiBuchungssatzTab";
import BelegScanTab from "./buchhaltung/BelegScanTab";
import SellerCommissionsTab from "./buchhaltung/SellerCommissionsTab";
import ArbeitszeitenTab from "./buchhaltung/ArbeitszeitenTab";
import WarrantiesOpenTab from "./buchhaltung/WarrantiesOpenTab";
import DatevMailModal from "../components/buchhaltung/DatevMailModal";
import CashBookSection from "../components/buchhaltung/CashBookSection";
import ReminderMailModal from "./buchhaltung/ReminderMailModal";

function Kpi({ label, value, cls = "text-[#111827]", testid, onClick, active }) {
    const clickable = typeof onClick === "function";
    return (
        <div
            onClick={onClick}
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
            className={`rounded-sm border bg-white px-4 py-3 transition-colors ${clickable ? "cursor-pointer hover:border-[#FFCC00] hover:bg-[#FFFDF5]" : ""} ${active ? "border-[#FFCC00] ring-2 ring-[#FFCC00]/40" : "border-gray-200"}`}
            data-testid={testid}>
            <div className={`text-2xl font-black font-mono-num leading-none ${cls}`}>{value}</div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mt-1.5">{label}</div>
        </div>
    );
}

export default function BuchhaltungPage() {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const tab = searchParams.get("tab") || "offene-posten";
    const [data, setData] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [mahnlaufBusy, setMahnlaufBusy] = useState(false);
    // Jul-2026 (User-Wunsch): EC-Beleg-Modus über Kopf-Buttons (neben Mahnlauf);
    // ?ecmode=digital springt direkt zum Terminal-/SumUp-Abgleich (Dashboard-Hinweis).
    const [ecMode, setEcMode] = useState(() => (searchParams.get("ecmode") === "digital" ? "digital" : "scan"));
    const [reminderMailItem, setReminderMailItem] = useState(null);
    const [orgBranch, setOrgBranch] = useState(null);
    // Jun-2026 (User-Wunsch) — anklickbare KPIs (Aging-Filter) + Universal-Suche.
    const [opFilter, setOpFilter] = useState(null);
    const [opSearch, setOpSearch] = useState("");

    const filteredItems = useMemo(() => {
        let items = data?.items || [];
        const od = (i) => Number(i.days_overdue || 0);
        if (opFilter === "overdue") items = items.filter((i) => od(i) > 0);
        else if (opFilter === "not_due") items = items.filter((i) => od(i) <= 0);
        else if (opFilter === "d1_30") items = items.filter((i) => od(i) >= 1 && od(i) <= 30);
        else if (opFilter === "d60_plus") items = items.filter((i) => od(i) > 60);
        const q = opSearch.trim().toLowerCase();
        if (q) {
            items = items.filter((i) => [
                i.invoice_number, i.customer_name, i.due_date,
                i.open_amount != null ? String(i.open_amount) : "",
                i.days_overdue ? `${i.days_overdue} tage überfällig` : "",
                i.reminder_level ? `mahnstufe ${i.reminder_level}` : "",
            ].filter(Boolean).join(" ").toLowerCase().includes(q));
        }
        return items;
    }, [data, opFilter, opSearch]);

    const toggleFilter = (key) => setOpFilter((cur) => (cur === key ? null : key));

    useEffect(() => {
        setApiOrgBranch(orgBranch);
        return () => setApiOrgBranch(null);
    }, [orgBranch]);

    const setTab = (t) => { const n = new URLSearchParams(searchParams); n.set("tab", t); setSearchParams(n); };

    const [auditBusy, setAuditBusy] = useState(false);
    const checkNumberAudit = async () => {
        setAuditBusy(true);
        try {
            const y = String(new Date().getFullYear());
            const { data } = await api.get(`/invoices-number-audit?year=${y}`);
            if (data.ok) {
                toast.success(`GoBD ${y}: Nummernkreis lückenlos (${data.total_invoices} Rechnungen).`);
            } else {
                const g = (data.groups || []).filter((x) => x.missing_count > 0)
                    .map((x) => `${x.location}: ${x.missing_count} fehlend (z. B. ${x.missing_numbers.slice(0, 5).join(", ")})`).join(" · ");
                toast.warning(`GoBD ${y}: ${data.total_missing} Lücke(n) im Nummernkreis — ${g}`, { duration: 9000 });
            }
        } catch { toast.error("Nummernkreis-Prüfung fehlgeschlagen"); }
        finally { setAuditBusy(false); }
    };

    const load = useCallback(async () => {
        try {
            const r = await api.get("/buchhaltung/open-items");
            setData(r.data);
        } catch {
            setData({ items: [], summary: { count: 0, total_open: 0, overdue_count: 0, aging: {} } });
        }
    }, [orgBranch]);
    useEffect(() => { load(); }, [load]);

    const mahnen = async (id) => {
        setBusyId(id);
        try {
            const r = await api.post(`/invoices/${id}/reminder`, { send_email: false });
            toast.success(`Mahnstufe ${r.data?.reminder?.level} erstellt.`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Mahnung fehlgeschlagen.");
        } finally {
            setBusyId(null);
        }
    };

    const openReminderPdf = async (id, level) => {
        try {
            const r = await api.get(`/invoices/${id}/reminder/${level}/pdf`, { responseType: "blob" });
            const inv = (data?.items || []).find((x) => x.id === id || x.invoice_id === id);
            previewPdfResponse(r, `Mahnung_St${level}.pdf`, { recipient: customerRecipient(inv?.customer || inv) });
        } catch {
            toast.error("Mahn-PDF konnte nicht geladen werden.");
        }
    };

    const startMahnlauf = async () => {
        if (!window.confirm("Mahnlauf für alle überfälligen offenen Posten starten?")) return;
        setMahnlaufBusy(true);
        try {
            const r = await api.post("/buchhaltung/mahnlauf", { min_days_overdue: 1 });
            toast.success(`Mahnlauf: ${r.data?.reminded_count ?? 0} Mahnung(en) erstellt.`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Mahnlauf fehlgeschlagen.");
        } finally {
            setMahnlaufBusy(false);
        }
    };

    const sammelPdf = async () => {
        try {
            const r = await api.get("/buchhaltung/mahnungen/sammel-pdf", { responseType: "blob" });
            previewPdfResponse(r, "Mahnlauf-Sammel.pdf");
        } catch {
            toast.error("Keine offenen Mahnungen für ein Sammel-PDF vorhanden.");
        }
    };

    // DATEV-Export per Menü (Header-Dropdown → Buchhaltung): Modal direkt
    // anhand des ?action=datev-Parameters initial öffnen (kein Effekt nötig).
    const [datevOpen, setDatevOpen] = useState(searchParams.get("action") === "datev");
    const closeDatev = () => {
        setDatevOpen(false);
        if (searchParams.get("action") === "datev") {
            const n = new URLSearchParams(searchParams);
            n.delete("action");
            setSearchParams(n, { replace: true });
        }
    };
    const [datevMailOpen, setDatevMailOpen] = useState(false);

    const s = data?.summary || {};
    const aging = s.aging || {};

    return (
        <div className="min-h-screen bg-gray-50">
            <EmployeeContextBanner />
            <div className="bg-[#111827] text-white">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
                    <Link to="/dashboard" className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid="buchhaltung-back">
                        <ArrowLeft className="w-3.5 h-3.5" /> Dashboard
                    </Link>
                    <div className="flex items-start justify-between gap-6 mt-4 flex-wrap">
                        <div className="flex-1 min-w-0">
                            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight flex items-center gap-3" data-testid="buchhaltung-title">
                                <Calculator className="w-9 h-9 text-[#FFCC00]" /> Buchhaltung
                                <SettingsShortcut to="/dashboard?tab=settings#settings-finanzen" title="Finanz-Einstellungen" testid="settings-shortcut-buchhaltung" dark />
                            </h1>
                            <p className="text-sm text-gray-400 mt-2 truncate max-w-full">
                                Offene Posten, Mahnwesen, Zahlungs-/Bankimport und DATEV-Export an einem Ort.
                            </p>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <OrgBranchSwitcher user={user} value={orgBranch} onChange={setOrgBranch} allowAll allowReorder dark />
                            {orgBranch === "__all__" && (
                                <span className="text-[10px] font-bold uppercase tracking-wider text-amber-300 bg-amber-900/30 border border-amber-700 rounded-sm px-2 py-1" data-testid="buchhaltung-all-branches-hint">
                                    Aggregat – Aktionen je Standort wählen
                                </span>
                            )}
                            {/* Jul-2026 (User-Wunsch): Mahnwesen in EINEM Button vereint —
                                Mahnlauf (Stufen erhöhen & versenden) vs. Sammel-PDF (nur drucken) */}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button type="button" className="h-9 rounded-sm bg-[#FFCC00] hover:bg-[#e6b800] text-[#111827] text-sm font-bold" data-testid="buchhaltung-mahnwesen-btn">
                                        {mahnlaufBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Gavel className="w-4 h-4 mr-1.5" />}
                                        Mahnwesen
                                        <ChevronDown className="w-3.5 h-3.5 ml-1.5" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                    <DropdownMenuItem onClick={startMahnlauf} disabled={mahnlaufBusy} data-testid="buchhaltung-mahnlauf-btn" title="Prüft alle überfälligen Rechnungen, erhöht die Mahnstufe und erzeugt/versendet die Mahnungen.">
                                        <Gavel className="w-3.5 h-3.5 mr-2" /> Mahnlauf starten
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={sammelPdf} data-testid="buchhaltung-sammelpdf-btn" title="Druckt alle aktuellen Mahnungen gesammelt als EIN PDF — ohne Mahnstufen zu verändern.">
                                        <FileDown className="w-3.5 h-3.5 mr-2" /> Mahnungen-Sammel-PDF (nur drucken)
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                            {/* Jul-2026 (User-Wunsch): EIN „EC-Belege"-Button mit Auswahl */}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button type="button" variant="outline"
                                        className={`h-9 rounded-sm text-sm font-bold ${tab === "ec-belege" ? "bg-[#111827] text-[#FFCC00] border-[#111827] hover:bg-black" : "bg-white text-[#111827] hover:bg-gray-100"}`}
                                        data-testid="buchhaltung-ec-belege-btn">
                                        <ScanLine className="w-4 h-4 mr-1.5" /> EC-Belege
                                        <ChevronDown className="w-3.5 h-3.5 ml-1.5" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                    <DropdownMenuItem onClick={() => { setEcMode("scan"); setTab("ec-belege"); }} data-testid="buchhaltung-ec-scan-btn">
                                        <ScanLine className="w-3.5 h-3.5 mr-2" /> EC-Belege scannen (KI)
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => { setEcMode("digital"); setTab("ec-belege"); }} data-testid="buchhaltung-ec-digital-btn">
                                        <Plug className="w-3.5 h-3.5 mr-2" /> EC-Digital vom Terminal
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                            {/* Jul-2026 (User-Wunsch): EIN Einstieg für Eingangsrechnungen —
                                KI-Scan (PDF/Bild) + E-Rechnung (XML) vereint im Beleg-Scan-Tab */}
                            <Button type="button" onClick={() => setTab("beleg-scan")}
                                variant="outline"
                                className={`h-9 rounded-sm text-sm font-bold ${tab === "beleg-scan" ? "bg-[#111827] text-[#FFCC00] border-[#111827] hover:bg-black" : "bg-white text-[#111827] hover:bg-gray-100"}`}
                                data-testid="buchhaltung-eingangsrechnung-btn">
                                <FileDown className="w-4 h-4 mr-1.5" /> Eingangsrechnung (KI / E-Rechnung)
                            </Button>
                            {/* Jul-2026 (User-Wunsch): seltener genutzte Funktionen gebündelt */}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button type="button" variant="outline" className="h-9 rounded-sm text-sm font-bold bg-white text-[#111827] hover:bg-gray-100" data-testid="buchhaltung-more-btn">
                                        Weitere Funktionen
                                        <ChevronDown className="w-3.5 h-3.5 ml-1.5" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                    <DropdownMenuItem onClick={checkNumberAudit} disabled={auditBusy} data-testid="buchhaltung-number-audit-btn">
                                        {auditBusy ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5 mr-2" />} GoBD-Nummernkreis
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setDatevOpen(true)} data-testid="buchhaltung-datev-btn">
                                        <FileSpreadsheet className="w-3.5 h-3.5 mr-2" /> Steuerberater-Export
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => { window.location.href = "/dealer/gelangensnachweis"; }} data-testid="buchhaltung-gn-link">
                                        <Truck className="w-3.5 h-3.5 mr-2" /> Gelangensnachweise
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                </div>
            </div>
            <div className="max-w-7xl mx-auto px-4 py-8">

                {/* KPIs — anklickbar als Aging-Filter */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
                    <Kpi label="Offen gesamt" value={formatPrice(s.total_open || 0)} testid="buchhaltung-kpi-total" onClick={() => setOpFilter(null)} active={opFilter === null} />
                    <Kpi label="Offene Posten" value={s.count || 0} testid="buchhaltung-kpi-count" onClick={() => setOpFilter(null)} active={opFilter === null} />
                    <Kpi label="Überfällig" value={s.overdue_count || 0} cls={s.overdue_count > 0 ? "text-red-600" : "text-[#111827]"} testid="buchhaltung-kpi-overdue" onClick={() => toggleFilter("overdue")} active={opFilter === "overdue"} />
                    <Kpi label="Nicht fällig" value={formatPrice(aging.not_due || 0)} cls="text-emerald-700" testid="buchhaltung-kpi-notdue" onClick={() => toggleFilter("not_due")} active={opFilter === "not_due"} />
                    <Kpi label="1–30 Tage" value={formatPrice(aging.d1_30 || 0)} cls="text-amber-600" testid="buchhaltung-kpi-d1-30" onClick={() => toggleFilter("d1_30")} active={opFilter === "d1_30"} />
                    <Kpi label="> 60 Tage" value={formatPrice(aging.d60_plus || 0)} cls="text-red-600" testid="buchhaltung-kpi-d60" onClick={() => toggleFilter("d60_plus")} active={opFilter === "d60_plus"} />
                </div>

                {/* Universal-Suche (durchsucht RG-Nr, Kunde, Betrag, Fälligkeit, Mahnstufe) + Schnellzugriff Importe */}
                {tab === "offene-posten" && (
                    <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="buchhaltung-search-row">
                        <div className="relative flex-1 min-w-[260px] max-w-md" data-testid="buchhaltung-search-wrap">
                            <Search className="w-4 h-4 text-emerald-600 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                            <Input
                                value={opSearch}
                                onChange={(e) => setOpSearch(e.target.value)}
                                placeholder="Suchen: Rechnungs-Nr., Kunde, Betrag, Fälligkeit, Mahnstufe …"
                                className="rounded-sm pl-9 pr-9 h-9 border-2 border-emerald-500 focus:border-emerald-600 focus-visible:ring-emerald-400 placeholder:text-emerald-700/70"
                                data-testid="buchhaltung-universal-search"
                            />
                            {opSearch && (
                                <button type="button" onClick={() => setOpSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700" data-testid="buchhaltung-search-clear">
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        <Link to="/dealer/bank-import">
                            <Button type="button" variant="outline" className="h-9 rounded-sm text-sm font-bold" data-testid="buchhaltung-bankimport-link">
                                <Landmark className="w-4 h-4 mr-1.5" /> Bankimport
                            </Button>
                        </Link>
                        <Button type="button" onClick={() => setTab("zahlungen")} variant="outline" className="h-9 rounded-sm text-sm font-bold" data-testid="buchhaltung-zahlungsimport-quick">
                            <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Zahlungsimport
                        </Button>
                        <Button type="button" onClick={() => setTab("ec-belege")} variant="outline" className="h-9 rounded-sm text-sm font-bold" data-testid="buchhaltung-ec-belege-quick">
                            <FileArchive className="w-4 h-4 mr-1.5" /> EC-Belege
                        </Button>
                    </div>
                )}

                {/* Tabs */}
                <div className="flex items-center gap-2 mb-4 border-b border-gray-200" data-testid="buchhaltung-tabs">
                    {[
                        { key: "offene-posten", label: "Debitoren" },
                        { key: "kreditoren", label: "Kreditoren" },
                        { key: "kassenbuch", label: "Kassenbuch" },
                        { key: "beleg-scan", label: "Beleg-Scan (KI)" },
                        { key: "ki-buchungssatz", label: "Buchungssatz" },
                        { key: "boni", label: "Boni offen" },
                        { key: "garantien", label: "Garantien offen" },
                        { key: "bank-gelder", label: "Bank offen" },
                        { key: "interne-rechnungen", label: "Interne RG prüfen" },
                        { key: "provisionen", label: "Provisionen" },
                        { key: "arbeitszeiten", label: "Arbeitszeiten" },
                    ].map((t) => (
                        <button key={t.key} type="button" onClick={() => setTab(t.key)}
                            className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px transition-colors ${tab === t.key ? "border-[#FFCC00] text-[#111827]" : "border-transparent text-gray-500 hover:text-[#111827]"}`}
                            data-testid={`buchhaltung-tab-${t.key}`}>
                            {t.label}
                        </button>
                    ))}
                </div>

                {tab === "offene-posten" && (
                    <OpenItemsTable data={{ ...data, items: filteredItems }} busyId={busyId} onMahnen={mahnen} onMahnenMail={setReminderMailItem} onPdf={openReminderPdf} />
                )}
                <div key={orgBranch || "self"}>
                {tab === "kreditoren" && (
                    <KreditorenTab />
                )}
                {tab === "kassenbuch" && (
                    <CashBookSection />
                )}
                {tab === "beleg-scan" && (
                    <BelegScanTab />
                )}
                {tab === "zahlungen" && (
                    <PaymentsImport onDone={load} />
                )}
                {tab === "ec-belege" && (
                    <ECReceiptsTab mode={ecMode} />
                )}
                {tab === "boni" && (
                    <BoniTrackingTab />
                )}
                {tab === "garantien" && (
                    <WarrantiesOpenTab />
                )}
                {tab === "bank-gelder" && (
                    <OutstandingBankFundsTab />
                )}
                {tab === "interne-rechnungen" && (
                    <InternalInvoicesTab />
                )}
                {tab === "provisionen" && (
                    <SellerCommissionsTab />
                )}
                {tab === "arbeitszeiten" && (
                    <ArbeitszeitenTab />
                )}
                {tab === "ki-buchungssatz" && (
                    <KiBuchungssatzTab />
                )}
                </div>
            </div>
            {datevOpen && <DatevExportModal onClose={closeDatev} onMail={() => setDatevMailOpen(true)} />}
            {datevMailOpen && <DatevMailModal onClose={() => setDatevMailOpen(false)} />}
            {reminderMailItem && <ReminderMailModal item={reminderMailItem} onClose={() => setReminderMailItem(null)} onSent={load} />}
        </div>
    );
}

function DatevExportModal({ onClose, onMail }) {
    const { user } = useAuth();
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    const [from, setFrom] = useState(iso(new Date(today.getFullYear(), 0, 1)));
    const [to, setTo] = useState(iso(today));
    const [fmt, setFmt] = useState("uebersicht");
    const [skr, setSkr] = useState(user?.datev_skr || "03");
    const [kind, setKind] = useState("all");
    const [busy, setBusy] = useState(false);
    const [zipBusy, setZipBusy] = useState(false);
    const [gobdBusy, setGobdBusy] = useState(false);
    const [cgBusy, setCgBusy] = useState(false);

    const downloadComplianceGaps = async () => {
        setCgBusy(true);
        try {
            const r = await api.get("/invoices/compliance-gaps.pdf", { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `Compliance-Fruehwarnung_${new Date().toISOString().slice(0, 10)}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Compliance-Frühwarnung (PDF) für den Steuerberater erstellt.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Compliance-Report fehlgeschlagen.");
        } finally {
            setCgBusy(false);
        }
    };

    const downloadGobd = async () => {
        setGobdBusy(true);
        try {
            const r = await api.get("/invoices/gobd-export", {
                params: { date_from: from, date_to: to }, responseType: "blob",
            });
            const url = URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `GoBD-Export_${from}_${to}.zip`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("GoBD-Z3-Export erstellt (index.xml + CSVs für die Betriebsprüfung).");
        } catch (e) {
            toast.error(e.response?.data?.detail || "GoBD-Export fehlgeschlagen.");
        } finally {
            setGobdBusy(false);
        }
    };

    const downloadBelegtransfer = async () => {
        setZipBusy(true);
        try {
            const r = await api.get("/invoices/datev-belegtransfer.zip", { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `DATEV_Belegtransfer_${new Date().toISOString().slice(0, 10)}.zip`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("DATEV-Belegtransfer-ZIP heruntergeladen (XML + PDFs)");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Keine finalisierten Rechnungen im Zeitraum");
        } finally {
            setZipBusy(false);
        }
    };

    const quick = (key) => {
        const y = today.getFullYear();
        const map = {
            this_month: [new Date(y, today.getMonth(), 1), new Date(y, today.getMonth() + 1, 0)],
            last_month: [new Date(y, today.getMonth() - 1, 1), new Date(y, today.getMonth(), 0)],
            q1: [new Date(y, 0, 1), new Date(y, 2, 31)],
            q2: [new Date(y, 3, 1), new Date(y, 5, 30)],
            q3: [new Date(y, 6, 1), new Date(y, 8, 30)],
            q4: [new Date(y, 9, 1), new Date(y, 11, 31)],
            this_year: [new Date(y, 0, 1), new Date(y, 11, 31)],
            last_year: [new Date(y - 1, 0, 1), new Date(y - 1, 11, 31)],
        };
        const [a, b] = map[key];
        setFrom(iso(a)); setTo(iso(b));
    };

    const download = async () => {
        setBusy(true);
        try {
            const r = await api.get("/buchhaltung/export/datev", {
                params: { date_from: from, date_to: to, kind, format: fmt, skr },
                responseType: "blob",
            });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${fmt === "extf" ? "DATEV-Buchungsstapel" : "Buchhaltung-Export"}_${from}_bis_${to}.csv`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success(fmt === "extf" ? "DATEV-Buchungsstapel erstellt." : "Steuerberater-Export (CSV) erstellt.");
            onClose?.();
        } catch {
            toast.error("Export fehlgeschlagen.");
        } finally {
            setBusy(false);
        }
    };

    const QUICK = [
        ["this_month", "Dieser Monat"], ["last_month", "Letzter Monat"],
        ["q1", "Q1"], ["q2", "Q2"], ["q3", "Q3"], ["q4", "Q4"],
        ["this_year", "Lfd. Jahr"], ["last_year", "Vorjahr"],
    ];

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto" data-testid="datev-export-modal">
            <div className="w-full max-w-lg mt-12 bg-white border border-gray-200 rounded-sm shadow-xl">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-[#111827] text-white rounded-t-sm">
                    <h2 className="font-display text-lg font-black">Steuerberater-Export</h2>
                    <button type="button" onClick={onClose} className="p-1 rounded-sm hover:bg-white/10" data-testid="datev-close">✕</button>
                </div>
                <div className="p-5 space-y-4">
                    <div>
                        <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Zeitraum-Schnellauswahl</label>
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {QUICK.map(([k, l]) => (
                                <button key={k} type="button" onClick={() => quick(k)} className="px-2.5 h-7 rounded-sm border border-gray-300 text-[11px] font-bold text-gray-700 hover:border-[#111827] hover:bg-gray-50" data-testid={`datev-quick-${k}`}>{l}</button>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Von</label>
                            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 rounded-sm mt-1" data-testid="datev-from" />
                        </div>
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Bis</label>
                            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 rounded-sm mt-1" data-testid="datev-to" />
                        </div>
                    </div>
                    <div>
                        <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Format</label>
                        <div className="grid grid-cols-2 gap-2 mt-1.5">
                            <button type="button" onClick={() => setFmt("uebersicht")} className={`px-3 py-2 rounded-sm border text-left text-xs font-bold ${fmt === "uebersicht" ? "border-[#111827] bg-gray-50" : "border-gray-300 text-gray-600"}`} data-testid="datev-fmt-uebersicht">
                                Übersicht (CSV)<span className="block font-normal text-[10px] text-gray-500">Lesbar, mit Netto/USt/Summen</span>
                            </button>
                            <button type="button" onClick={() => setFmt("extf")} className={`px-3 py-2 rounded-sm border text-left text-xs font-bold ${fmt === "extf" ? "border-[#111827] bg-gray-50" : "border-gray-300 text-gray-600"}`} data-testid="datev-fmt-extf">
                                DATEV-Buchungsstapel<span className="block font-normal text-[10px] text-gray-500">EXTF 700 — Direktimport Kanzlei</span>
                            </button>
                        </div>
                    </div>
                    {fmt === "extf" && (
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Kontenrahmen</label>
                            <select value={skr} onChange={(e) => setSkr(e.target.value)} className="w-full h-9 text-sm rounded-sm border border-gray-300 px-2 mt-1 focus:border-[#111827] focus:outline-none" data-testid="datev-skr" aria-label="Kontenrahmen (SKR) wählen">
                                <option value="03">SKR03</option>
                                <option value="04">SKR04</option>
                            </select>
                        </div>
                    )}
                    <div>
                        <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Umfang</label>
                        <select value={kind} onChange={(e) => setKind(e.target.value)} className="w-full h-9 text-sm rounded-sm border border-gray-300 px-2 mt-1 focus:border-[#111827] focus:outline-none" data-testid="datev-kind" aria-label="Export-Art wählen">
                            <option value="all">Debitoren & Kreditoren</option>
                            <option value="debitoren">Nur Debitoren (Ausgangsrechnungen)</option>
                            <option value="kreditoren">Nur Kreditoren (Eingangsrechnungen)</option>
                        </select>
                    </div>
                    <div className="border-t border-gray-200 pt-3">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Betriebsprüfung (GoBD / GDPdU)</label>
                        <Button type="button" variant="outline" onClick={downloadGobd} disabled={gobdBusy} className="w-full h-9 rounded-sm text-xs font-bold mt-1.5" data-testid="gobd-z3-export-btn">
                            {gobdBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <FileArchive className="w-4 h-4 mr-1.5" />} GoBD-Z3-Export (Datenträgerüberlassung)
                        </Button>
                        <p className="text-[10px] text-gray-400 mt-1">ZIP für den Betriebsprüfer: index.xml (GDPdU) + rechnungen.csv + positionen.csv + Verfahrensdoku. Einlesbar in IDEA/ACL.</p>
                        <Button type="button" variant="outline" onClick={downloadComplianceGaps} disabled={cgBusy} className="w-full h-9 rounded-sm text-xs font-bold mt-2" data-testid="compliance-gaps-pdf-btn">
                            {cgBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <ShieldAlert className="w-4 h-4 mr-1.5" />} Compliance-Frühwarnung (PDF)
                        </Button>
                        <p className="text-[10px] text-gray-400 mt-1">Steuerberater-Report: offene Gelangensnachweise, fehlende USt-IdNr., nicht festgeschriebene Entwürfe &amp; Reverse-Charge-Lücken. Wird dem monatlichen Belegtransfer automatisch beigelegt (nur bei Lücken).</p>
                    </div>
                    <div className="border-t border-gray-200 pt-3">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Belegtransfer (PDFs) &amp; Versand</label>
                        <div className="grid grid-cols-2 gap-2 mt-1.5">
                            <Button type="button" variant="outline" onClick={downloadBelegtransfer} disabled={zipBusy} className="h-9 rounded-sm text-xs font-bold" data-testid="datev-belegtransfer-btn">
                                {zipBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <FileArchive className="w-4 h-4 mr-1.5" />} Belegtransfer-ZIP
                            </Button>
                            <Button type="button" variant="outline" onClick={() => { onClose?.(); onMail?.(); }} className="h-9 rounded-sm text-xs font-bold" data-testid="datev-mail-btn">
                                <Mail className="w-4 h-4 mr-1.5" /> Per E-Mail an Steuerberater
                            </Button>
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1">ZIP = XML-Manifest + Rechnungs-PDFs für den DATEV-Belegtransfer.</p>
                    </div>
                </div>
                <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-sm">
                    <Button type="button" variant="outline" onClick={onClose} className="h-9 rounded-sm text-sm" data-testid="datev-cancel">Abbrechen</Button>
                    <Button type="button" onClick={download} disabled={busy} className="h-9 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black text-sm font-bold" data-testid="datev-download">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <FileSpreadsheet className="w-4 h-4 mr-1.5" />} Export herunterladen
                    </Button>
                </div>
            </div>
        </div>
    );
}
